import { useState } from "react";

import { MicIcon, speakingScoreClass } from "./SpeakingPanel";
import useSentenceSpeaking from "./useSentenceSpeaking";

// 본문 쪽 문장 끝에 다는 마이크(2026-10-18 대표). 지인에듀가 문장마다 마지막 문장부호 자리(sentences[].end)를
// 주면 그 위에 작은 마이크를 얹는다. 누르면 그 문장을 들려주고, 다 들으면 마이크가 아이 차례로 바뀐다.
// 한 번 더 누르면 따라 읽기를 듣는다(마이크는 아이가 누른 자리에서 연다. iOS 는 그 밖에서 열면 막힌다).
// 읽고 나면 마이크 옆에 가장 잘 읽은 점수가 남는다. 받아쓴 글은 보이지 않는다.

const keyOf = (audioUrl, sentenceIndex) => `${audioUrl}#${sentenceIndex}`;

const SentenceMicLayer = ({ pageNumber, audioList, speaking, onResult, isEditable }) => {
  const [readyKey, setReadyKey] = useState(null);
  const markList = audioList.flatMap((audio) =>
    (audio.sentences || [])
      .filter((sentence) => sentence.end && (sentence.end.page === undefined || sentence.end.page === pageNumber))
      .map((sentence) => ({ audio, sentence, key: keyOf(audio.url, sentence.idx) }))
  );
  const audioByKey = Object.fromEntries(markList.map((mark) => [mark.key, mark.audio]));

  const { isSupported, listeningKey, playingKey, play, stopPlaying, record } = useSentenceSpeaking({
    sentenceList: [],
    // 같은 쪽에 음원이 둘이면 문장 idx 가 겹친다. 어느 음원의 문장인지는 key 로 가른다.
    onResult: (sentence, accuracy, transcript, key) => {
      const audio = audioByKey[key];
      if (audio) onResult(audio.url, sentence.idx, accuracy, transcript);
    },
  });
  const canSpeak = isSupported && isEditable;

  if (!markList.length) return null;

  const handleClick = (mark) => {
    if (listeningKey === mark.key) {
      record(mark.sentence, mark.key, mark.audio.sentences);
      return;
    }
    if (listeningKey !== null) return;
    if (readyKey === mark.key && canSpeak) {
      setReadyKey(null);
      record(mark.sentence, mark.key, mark.audio.sentences);
      return;
    }
    if (playingKey === mark.key) {
      stopPlaying();
      return;
    }
    setReadyKey(null);
    play(mark.sentence, mark.key, () => {
      if (canSpeak) setReadyKey(mark.key);
    });
  };

  return (
    <div className="sentence-mic-layer">
      {markList.map((mark) => {
        const spoken = (speaking[mark.audio.url] || {})[mark.sentence.idx];
        const score = spoken?.best_accuracy ?? spoken?.last_accuracy;
        const state =
          listeningKey === mark.key
            ? "is-listening"
            : playingKey === mark.key
              ? "is-playing"
              : readyKey === mark.key
                ? "is-ready"
                : typeof score === "number"
                  ? "is-done"
                  : "";
        return (
          <span
            key={mark.key}
            className="sentence-mic"
            style={{ left: `${mark.sentence.end.x * 100}%`, top: `${mark.sentence.end.y * 100}%` }}
          >
            <button
              type="button"
              className={`sentence-mic__button ${state}`}
              onClick={() => handleClick(mark)}
              aria-label={listeningKey === mark.key ? "다 읽었어요" : "듣고 따라 말하기"}
            >
              {playingKey === mark.key ? <SpeakerIcon /> : <MicIcon size={10} />}
            </button>
            {typeof score === "number" && listeningKey !== mark.key && (
              <span className={`sentence-mic__score ${speakingScoreClass(score)}`}>{score}</span>
            )}
          </span>
        );
      })}
    </div>
  );
};

const SpeakerIcon = () => (
  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
    <path d="M4 9h4l5-4v14l-5-4H4z" />
    <path d="M16 9a4 4 0 0 1 0 6M19 6a8 8 0 0 1 0 12" />
  </svg>
);

export default SentenceMicLayer;
